import { calculateAnthropometry, resolvePatientAge } from "./antropometria.js";

export const objectiveRules = {
  perdida_grasa: {
    label: "Perdida de grasa",
    calorieAdjustment: 0,
    proteinGkg: 1.8,
    carbGkg: 3,
    fatGkg: 0.8,
    fatPercent: 25,
  },
  aumento_masa: {
    label: "Aumento de masa muscular",
    calorieAdjustment: 0,
    proteinGkg: 1.8,
    carbGkg: 5,
    fatGkg: 1,
    fatPercent: 25,
  },
  mantenimiento: {
    label: "Mantenimiento",
    calorieAdjustment: 0,
    proteinGkg: 1.6,
    carbGkg: 4,
    fatGkg: 0.9,
    fatPercent: 30,
  },
};

export const nafCatalog = {
  ligera: {
    label: "Ligera",
    range: "1.40-1.69",
    male: 1.55,
    female: 1.56,
    description: "Actividad diaria ligera segun rangos RIEN/FAO PAL.",
  },
  moderada: {
    label: "Moderada",
    range: "1.70-1.99",
    male: 1.78,
    female: 1.64,
    description: "Actividad moderada segun rangos RIEN/FAO PAL.",
  },
  fuerte: {
    label: "Fuerte",
    range: "2.00-2.40",
    male: 2.1,
    female: 1.82,
    description: "Actividad alta/fuerte segun rangos RIEN/FAO PAL.",
  },
};

export const activityToNafCategory = {
  bajo: "ligera",
  medio: "moderada",
  alto: "fuerte",
};

export const activityFactors = {
  bajo: nafCatalog.ligera.female,
  medio: nafCatalog.moderada.male,
  alto: nafCatalog.fuerte.male,
};

export function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundNumber(value, decimals = 0) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function positiveNumber(value) {
  const parsed = toNumber(value);
  return parsed > 0 ? parsed : 0;
}

function patientSexKey(patient) {
  return patient?.sexo === "masculino" ? "male" : "female";
}

export function getNafCategory(patient) {
  const explicit = String(patient?.naf_categoria ?? "").trim().toLowerCase();
  if (nafCatalog[explicit]) return explicit;
  return activityToNafCategory[patient?.actividad] ?? "moderada";
}

export function getSuggestedNaf(patient) {
  const category = getNafCategory(patient);
  const record = nafCatalog[category] ?? nafCatalog.moderada;
  return record[patientSexKey(patient)] ?? record.female;
}

export function getNafValue(patient) {
  const manual = positiveNumber(patient?.naf_valor);
  return {
    category: getNafCategory(patient),
    suggested: getSuggestedNaf(patient),
    value: manual || getSuggestedNaf(patient),
    isManual: manual > 0,
  };
}

function estimateLeanMass(patient) {
  const anthropometry = calculateAnthropometry(patient);
  const calculatedLeanMass = anthropometry.derivedBasics?.leanMass;
  if (calculatedLeanMass?.value) {
    return {
      value: calculatedLeanMass.value,
      source: `antropometria_${calculatedLeanMass.source}`,
    };
  }
  const explicitLeanMass = positiveNumber(patient?.masa_libre_grasa_kg);
  if (explicitLeanMass) {
    return { value: explicitLeanMass, source: "manual" };
  }
  const weight = positiveNumber(patient?.peso_kg);
  const fatPercent = positiveNumber(patient?.porcentaje_grasa);
  if (weight && fatPercent > 0 && fatPercent < 80) {
    return {
      value: roundNumber(weight * (1 - fatPercent / 100), 2),
      source: "estimada_por_grasa",
    };
  }
  if (anthropometry.primaryComposition?.leanKg) {
    return {
      value: anthropometry.primaryComposition.leanKg,
      source: `antropometria_${anthropometry.primaryComposition.method}`,
    };
  }
  return { value: 0, source: "faltante" };
}

function getIdealWeight(patient) {
  const anthropometry = calculateAnthropometry(patient);
  if (anthropometry.derivedBasics?.idealWeightKg && String(anthropometry.derivedBasics.idealWeightSource || "").startsWith("metas_")) {
    return { value: anthropometry.derivedBasics.idealWeightKg, source: anthropometry.derivedBasics.idealWeightSource };
  }
  const explicitIdeal = positiveNumber(patient?.peso_ideal_kg);
  if (explicitIdeal) return { value: explicitIdeal, source: "peso_ideal_kg" };
  const objectiveWeight = positiveNumber(patient?.peso_objetivo_kg);
  if (objectiveWeight) return { value: objectiveWeight, source: "peso_objetivo_kg" };
  if (anthropometry.derivedBasics?.idealWeightKg) {
    return { value: anthropometry.derivedBasics.idealWeightKg, source: anthropometry.derivedBasics.idealWeightSource };
  }
  const currentWeight = positiveNumber(patient?.peso_kg);
  if (currentWeight) return { value: currentWeight, source: "peso_kg" };
  return { value: 0, source: "faltante" };
}

export function calculateCunninghamBmr(patient) {
  const leanMass = estimateLeanMass(patient);
  if (!leanMass.value) {
    return {
      value: 0,
      leanMassKg: 0,
      leanMassSource: leanMass.source,
      formula: "370 + 21.6 * masa_libre_grasa_kg",
    };
  }
  return {
    value: roundNumber(370 + 21.6 * leanMass.value),
    leanMassKg: leanMass.value,
    leanMassSource: leanMass.source,
    formula: "370 + 21.6 * masa_libre_grasa_kg",
  };
}

export function calculateHarrisBenedictBmr(patient) {
  const idealWeight = getIdealWeight(patient);
  const height = positiveNumber(patient?.talla_cm);
  const age = resolvePatientAge(patient);
  const formula = patient?.sexo === "masculino"
    ? "65.5 + 13.75 * peso_ideal_kg + 5.003 * talla_cm - 6.775 * edad"
    : "655.1 + 9.563 * peso_ideal_kg + 1.85 * talla_cm - 4.676 * edad";
  if (!idealWeight.value || !height || !age) {
    return {
      value: 0,
      idealWeightKg: idealWeight.value,
      idealWeightSource: idealWeight.source,
      formula,
    };
  }
  const value = patient?.sexo === "masculino"
    ? 65.5 + 13.75 * idealWeight.value + 5.003 * height - 6.775 * age
    : 655.1 + 9.563 * idealWeight.value + 1.85 * height - 4.676 * age;
  return {
    value: roundNumber(value),
    idealWeightKg: idealWeight.value,
    idealWeightSource: idealWeight.source,
    formula,
  };
}

export function calculateBmr(patient) {
  const harris = calculateHarrisBenedictBmr(patient);
  if (harris.value) return harris.value;
  return calculateCunninghamBmr(patient).value;
}

export function calculateGetAdjustments(patient) {
  const anthropometry = calculateAnthropometry(patient);
  const selectedTarget = anthropometry.targets?.selected ?? {};
  const adiposeKg = positiveNumber(patient?.kg_masa_adiposa_perder) || positiveNumber(selectedTarget.kgAdiposeToLose);
  const restrictionDays = positiveNumber(patient?.dias_restriccion);
  const muscleKg = positiveNumber(patient?.kg_masa_muscular_ganar) || positiveNumber(selectedTarget.kgMuscleToGain);
  const additionDays = positiveNumber(patient?.dias_adicion);

  const restrictionError = adiposeKg > 0 && restrictionDays <= 0
    ? "La restricción tiene kg de masa adiposa, pero requiere días de restricción mayores que cero."
    : "";
  const additionError = muscleKg > 0 && additionDays <= 0
    ? "La adición tiene kg de masa muscular, pero requiere días de adición mayores que cero."
    : "";

  const deficitTotalKcal = adiposeKg ? adiposeKg * (3500 / 0.454) : 0;
  const deficitDailyKcal = deficitTotalKcal && restrictionDays ? deficitTotalKcal / restrictionDays : 0;
  const adiposeLossGWeek = deficitDailyKcal ? ((deficitDailyKcal * 7) / 3500) * 454 : 0;

  const additionTotalKcal = muscleKg ? 7000 * muscleKg : 0;
  const additionDailyKcal = additionTotalKcal && additionDays ? additionTotalKcal / additionDays : 0;
  const muscleGainGWeek = additionDailyKcal ? ((additionDailyKcal * 7) / 7000) * 500 : 0;

  return {
    restriction: {
      kgAdiposeToLose: adiposeKg,
      days: restrictionDays,
      valid: !restrictionError,
      error: restrictionError,
      totalKcal: roundNumber(deficitTotalKcal),
      dailyKcal: roundNumber(deficitDailyKcal),
      gramsPerWeek: roundNumber(adiposeLossGWeek),
      source: positiveNumber(patient?.kg_masa_adiposa_perder) ? "manual" : selectedTarget.method ? `metas_${selectedTarget.method}` : "manual",
      formula: "(kg_masa_adiposa_perder * 3500 / 0.454) / dias_restriccion",
    },
    addition: {
      kgMuscleToGain: muscleKg,
      days: additionDays,
      valid: !additionError,
      error: additionError,
      totalKcal: roundNumber(additionTotalKcal),
      dailyKcal: roundNumber(additionDailyKcal),
      gramsPerWeek: roundNumber(muscleGainGWeek),
      source: positiveNumber(patient?.kg_masa_muscular_ganar) ? "manual" : selectedTarget.method ? `metas_${selectedTarget.method}` : "manual",
      formula: "(7000 * kg_masa_muscular_ganar) / dias_adicion",
    },
    valid: !restrictionError && !additionError,
    errors: [restrictionError, additionError].filter(Boolean),
  };
}

export function calculateEnergy(patient) {
  const cunningham = calculateCunninghamBmr(patient);
  const harrisBenedict = calculateHarrisBenedictBmr(patient);
  const naf = getNafValue(patient);
  const adjustments = calculateGetAdjustments(patient);

  const get1Cunningham = cunningham.value ? roundNumber(cunningham.value * naf.value) : 0;
  const get1Harris = harrisBenedict.value ? roundNumber(harrisBenedict.value * naf.value) : 0;
  const adjustmentKcal = adjustments.addition.dailyKcal - adjustments.restriction.dailyKcal;
  const get2CunninghamLiteral = adjustments.valid && get1Cunningham ? roundNumber(get1Cunningham + adjustmentKcal) : 0;
  const get2HarrisLiteral = adjustments.valid && get1Harris ? roundNumber(get1Harris + adjustmentKcal) : 0;
  const get2Suggested = get2HarrisLiteral || get2CunninghamLiteral || 0;
  const manualGet3 = positiveNumber(patient?.get_3);
  const finalEnergyRaw = manualGet3 || get2Suggested;
  const target = roundNumber(finalEnergyRaw);
  const bmr = harrisBenedict.value || cunningham.value || 0;
  const total = get1Harris || get1Cunningham || 0;
  const training = roundNumber(toNumber(patient?.entrenamientos_semana) * 90);
  const literalNotes = [
    "GET2 se calcula segun el Excel actualizado: GET1 - restriccion kcal/dia + adicion kcal/dia.",
  ];

  if (adjustments.errors.length) literalNotes.push(...adjustments.errors);
  if (!manualGet3) literalNotes.push("GET3 aun no ha sido confirmado por la profesional; se muestra GET2 como sugerencia y no se modifica con un piso oculto.");

  if (cunningham.leanMassSource === "estimada_por_grasa") {
    literalNotes.push("Masa libre de grasa estimada desde peso y porcentaje de grasa porque masa_libre_grasa_kg no fue diligenciada.");
  }
  if (String(cunningham.leanMassSource).startsWith("antropometria_")) {
    literalNotes.push("Masa libre de grasa estimada desde la valoración antropométrica avanzada porque masa_libre_grasa_kg y porcentaje_grasa manual no fueron diligenciados.");
  }
  if (harrisBenedict.idealWeightSource && !["peso_ideal_kg", "faltante"].includes(harrisBenedict.idealWeightSource)) {
    literalNotes.push(`Harris-Benedict usa ${harrisBenedict.idealWeightSource} como respaldo porque peso_ideal_kg esta vacio.`);
  }

  return {
    bmr,
    activityFactor: naf.value,
    occupationalAndExercise: Math.max(total - bmr, 0),
    trainingEstimate: training,
    total,
    adjustment: roundNumber(adjustmentKcal),
    target,
    finalEnergy: target,
    finalEnergySource: manualGet3 ? "get_3_manual" : "get2_sugerido",
    naf,
    cunningham,
    harrisBenedict,
    get1: {
      cunningham: get1Cunningham,
      harrisBenedict: get1Harris,
      formula: "TMB * NAF",
    },
    adjustments,
    get2: {
      cunningham: get2CunninghamLiteral,
      harrisBenedict: get2HarrisLiteral,
      suggested: get2Suggested,
      formula: "GET1 - restriccion_kcal_dia + adicion_kcal_dia",
    },
    get3: {
      manual: manualGet3,
      final: target,
      source: manualGet3 ? "manual" : "GET2 sugerido",
      confirmed: manualGet3 > 0,
      warning: manualGet3 > 0 && manualGet3 < 1200 ? "GET3 está por debajo del umbral orientativo configurado; el valor se conserva sin modificar." : "",
    },
    literalNotes,
  };
}

export function calculateMacros(patient) {
  const energy = calculateEnergy(patient);
  const weightReference = positiveNumber(patient.peso_kg) || positiveNumber(patient.peso_ideal_kg) || positiveNumber(patient.peso_objetivo_kg);
  const rule = objectiveRules[patient.objetivo_principal] ?? objectiveRules.mantenimiento;
  const proteinGkg = positiveNumber(patient.proteina_gkg) || rule.proteinGkg;
  const carbsGkg = positiveNumber(patient.carbohidrato_gkg) || rule.carbGkg;
  const fatGkg = positiveNumber(patient.grasa_gkg) || rule.fatGkg || 0;
  const protein = weightReference ? roundNumber(weightReference * proteinGkg) : 0;
  const carbs = weightReference ? roundNumber(weightReference * carbsGkg) : 0;
  const fat = weightReference ? roundNumber(weightReference * fatGkg) : 0;
  const proteinKcal = protein * 4;
  const carbsKcal = carbs * 4;
  const fatKcal = fat * 9;
  const totalMacroKcal = proteinKcal + carbsKcal + fatKcal;
  const activeTimes = activeMealTimes(patient);
  const manualMealCount = Math.round(positiveNumber(patient?.numero_tiempos_comida));
  const mealCount = manualMealCount || activeTimes.length || 0;
  const mealCountMismatch = manualMealCount > 0 && manualMealCount !== activeTimes.length;
  const vctDenominator = Math.max(totalMacroKcal, 1);
  return {
    calories: energy.target,
    protein,
    proteinGkg: Number(proteinGkg.toFixed(2)),
    carbs,
    carbsGkg: Number(carbsGkg.toFixed(2)),
    fat,
    fatGkg: Number(fatGkg.toFixed(2)),
    proteinKcal,
    carbsKcal,
    fatKcal,
    totalMacroKcal,
    proteinVct: roundNumber((proteinKcal / vctDenominator) * 100, 1),
    carbsVct: roundNumber((carbsKcal / vctDenominator) * 100, 1),
    fatVct: roundNumber((fatKcal / vctDenominator) * 100, 1),
    carbsVctWordLiteral: roundNumber((fatKcal / vctDenominator) * 100, 1),
    adequacyKcalPercent: energy.target ? roundNumber((totalMacroKcal / energy.target) * 100, 1) : 0,
    proteinPerMeal: mealCount > 0 ? roundNumber(protein / mealCount, 1) : 0,
    mealCount,
    manualMealCount,
    activeMealCount: activeTimes.length,
    mealCountMismatch,
    weightReference,
    table: [
      { macro: "Proteina", gKg: Number(proteinGkg.toFixed(2)), grams: protein, kcal: proteinKcal, vct: roundNumber((proteinKcal / vctDenominator) * 100, 1) },
      { macro: "Grasa", gKg: Number(fatGkg.toFixed(2)), grams: fat, kcal: fatKcal, vct: roundNumber((fatKcal / vctDenominator) * 100, 1) },
      { macro: "Carbohidratos", gKg: Number(carbsGkg.toFixed(2)), grams: carbs, kcal: carbsKcal, vct: roundNumber((carbsKcal / vctDenominator) * 100, 1) },
    ],
    notes: [
      "El Word referencia la formula de VCT de carbohidratos con kcal de grasa; la app muestra el VCT real de carbohidratos y conserva el valor literal como carbsVctWordLiteral en JSON.",
      ...(mealCountMismatch ? [`El número manual de tiempos (${manualMealCount}) no coincide con los tiempos activos (${activeTimes.length}); revisa la selección antes de entregar.`] : []),
    ],
  };
}

export function calculateCurrentIntake(patient) {
  const explicitCalories = Math.round(toNumber(patient.consumo_calorias_actuales));
  const protein = Math.round(toNumber(patient.consumo_proteina_actual_g));
  const carbs = Math.round(toNumber(patient.consumo_carbohidratos_actual_g));
  const fat = Math.round(toNumber(patient.consumo_grasas_actual_g));
  const caloriesFromMacros = Math.round(protein * 4 + carbs * 4 + fat * 9);
  const hasData = [explicitCalories, protein, carbs, fat].some((value) => value > 0);

  return {
    calories: explicitCalories || caloriesFromMacros,
    explicitCalories,
    caloriesFromMacros,
    protein,
    carbs,
    fat,
    hasData,
  };
}

export function validatePatient(patient) {
  const warnings = [];
  if (!toNumber(patient.peso_kg)) warnings.push("Falta peso actual para calcular requerimientos confiables.");
  if (!toNumber(patient.talla_cm)) warnings.push("Falta talla para estimar tasa metabólica basal.");
  if (!resolvePatientAge(patient)) warnings.push("Falta fecha de nacimiento para calcular la edad y estimar tasa metabólica basal.");
  if (!toNumber(patient.peso_ideal_kg) && !toNumber(patient.peso_objetivo_kg)) {
    warnings.push("Falta peso ideal o peso objetivo para Harris-Benedict literal del Word.");
  }
  const anthropometry = calculateAnthropometry(patient);
  const energy = calculateEnergy(patient);
  const macros = calculateMacros(patient);
  if (!toNumber(patient.masa_libre_grasa_kg) && !toNumber(patient.porcentaje_grasa) && !anthropometry.primaryComposition?.leanKg) {
    warnings.push("Falta masa libre de grasa o porcentaje de grasa para Cunningham.");
  }
  if (!patient.objetivo_principal) warnings.push("Selecciona un objetivo principal.");
  if (!patient.estado_nutricional) warnings.push("Define el diagnóstico nutricional antes de cerrar recomendaciones.");
  if (!patient.composicion_corporal && !toNumber(patient.peso_objetivo_kg)) {
    warnings.push("Completa composición corporal o peso objetivo para una mejor personalización.");
  }
  warnings.push(...energy.adjustments.errors);
  if (!energy.get3.confirmed && !energy.get2.suggested) {
    warnings.push("GET3 está pendiente y no hay un GET2 válido para sugerir una meta energética.");
  }
  if (macros.mealCountMismatch) {
    warnings.push(`El número manual de tiempos (${macros.manualMealCount}) no coincide con los tiempos activos (${macros.activeMealCount}).`);
  }
  const complex = `${patient.condiciones ?? ""}`.toLowerCase();
  if (/diabetes|renal|hepat|embarazo|trastorno|tca|cancer/.test(complex)) {
    warnings.push("Caso con condición clínica compleja: requiere revisión profesional especializada.");
  }
  const clinical = `${patient.enfermedades_condiciones ?? ""}`.toLowerCase();
  if (/diabetes|renal|hepat|cardi|hipertension|asma|ansiedad|depresi/.test(clinical)) {
    warnings.push("Condiciones medicas adicionales reportadas: revisar pautas clínicas específicas.");
  }
  const allergies = String(patient.alergias ?? "").trim();
  if (allergies) {
    warnings.push("Alergias / intolerancias reportadas: validar seguridad de sustituciones.");
  }
  if (!patient.nombre) warnings.push("Usa un identificador o nombre del paciente antes de exportar.");
  return warnings;
}

export function activeMealTimes(patient) {
  const manual = normalizePatientMealTimes(patient?.meal_times);
  if (manual.length) return manual;

  const base = ["desayuno", "media_manana", "almuerzo", "media_tarde", "cena"];
  if (toNumber(patient?.entrenamientos_semana) > 0) {
    base.splice(2, 0, "racion_preentrenamiento");
    base.splice(4, 0, "racion_postentrenamiento");
  }
  return base;
}

export const mealTimeCatalog = [
  { id: "desayuno", label: "Desayuno" },
  { id: "media_manana", label: "Media mañana" },
  { id: "racion_preentrenamiento", label: "Ración preentrenamiento" },
  { id: "racion_postentrenamiento", label: "Ración postentrenamiento" },
  { id: "almuerzo", label: "Almuerzo" },
  { id: "media_tarde", label: "Media tarde" },
  { id: "cena", label: "Cena" },
];

const validMealTimeIds = new Set(mealTimeCatalog.map((item) => item.id));

export function defaultMealTimes(patient) {
  return activeMealTimes({
    ...patient,
    meal_times: null,
  });
}

export function normalizePatientMealTimes(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? "").trim())
      .filter((item) => item && validMealTimeIds.has(item));
  }
  const text = String(value ?? "").trim();
  if (!text) return [];
  return text
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter((item) => item && validMealTimeIds.has(item));
}

const optionsByGroup = {
  proteinas: ["huevo", "pollo", "yogurt griego", "lentejas"],
  carbohidratos: ["avena", "arroz integral", "banano", "papa"],
  grasas: ["aguacate", "aceite de oliva", "frutos secos"],
};

const macroGroups = [
  {
    id: "proteinas",
    rowId: "proteina",
    label: "Proteina",
    macroKey: "protein",
    macroLabel: "proteina",
    portionMacroKey: "proteina_g",
    fallbackMacroPerPortion: 25,
    fallbackGrams: 100,
    fallbackMeasure: "1 porcion mediana",
  },
  {
    id: "carbohidratos",
    rowId: "carbo",
    label: "Carbohidrato",
    macroKey: "carbs",
    macroLabel: "carbohidratos",
    portionMacroKey: "carbohidratos_g",
    fallbackMacroPerPortion: 20,
    fallbackGrams: 30,
    fallbackMeasure: "1/2 taza cocida o equivalente",
  },
  {
    id: "grasas",
    rowId: "grasa",
    label: "Grasa saludable",
    macroKey: "fat",
    macroLabel: "grasas",
    portionMacroKey: "grasas_g",
    fallbackMacroPerPortion: 10,
    fallbackGrams: 10,
    fallbackMeasure: "2 cucharaditas",
  },
];

const mealTags = {
  desayuno: ["desayuno"],
  media_manana: ["media_manana", "media manana", "refrigerio", "fruta"],
  racion_preentrenamiento: ["racion_preentrenamiento", "preentrenamiento", "entrenamiento"],
  racion_postentrenamiento: ["racion_postentrenamiento", "postentrenamiento", "recuperacion"],
  almuerzo: ["almuerzo"],
  media_tarde: ["media_tarde", "media tarde", "refrigerio", "fruta"],
  cena: ["cena"],
};

function tableRows(data, tableName) {
  return data?.tables?.[tableName]?.rows ?? [];
}

function isActive(row) {
  return String(row?.estado ?? "activo").toLowerCase() !== "inactivo";
}

function normalizeText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function splitList(value) {
  return String(value ?? "")
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function groupIdFromValue(value) {
  const normalized = normalizeText(value);
  if (normalized.includes("prote")) return "proteinas";
  if (normalized.includes("carbo")) return "carbohidratos";
  if (normalized.includes("grasa")) return "grasas";
  return normalized;
}

function getPortionStandards(data) {
  const standards = Object.fromEntries(macroGroups.map((group) => [
    group.id,
    {
      grams: group.fallbackGrams,
      measure: group.fallbackMeasure,
      macroGrams: group.fallbackMacroPerPortion,
    },
  ]));

  for (const row of tableRows(data, "porciones").filter(isActive)) {
    const groupId = groupIdFromValue(row.grupo_alimento || row.categoria);
    const group = macroGroups.find((item) => item.id === groupId);
    if (!group) continue;
    const macroGrams = toNumber(row[group.portionMacroKey]) || group.fallbackMacroPerPortion;
    standards[group.id] = {
      grams: toNumber(row.gramos) || group.fallbackGrams,
      measure: row.medida_casera || group.fallbackMeasure,
      macroGrams,
      source: row.nombre || row.id,
    };
  }

  return standards;
}

function calculatePortionCount(targetMacro, macroPerPortion) {
  const portions = targetMacro / Math.max(macroPerPortion, 1);
  return Math.max(0.5, Number(portions.toFixed(1)));
}

function mealMatchesFood(food, meal) {
  const haystack = normalizeText(`${food.etiquetas ?? ""} ${food.categoria ?? ""} ${food.nombre ?? ""}`);
  const tags = mealTags[meal] ?? [meal];
  return tags.some((tag) => haystack.includes(normalizeText(tag)));
}

function patientFoodExclusions(patient) {
  return splitList([
    patient?.alimentos_no_consume,
    patient?.alergias,
    patient?.intolerancias,
    patient?.restricciones,
  ].filter(Boolean).join(","));
}

function foodAllowedForPatient(food, patient) {
  const exclusions = patientFoodExclusions(patient).map(normalizeText).filter(Boolean);
  if (!exclusions.length) return true;
  const foodText = normalizeText(`${food.nombre ?? ""} ${food.categoria ?? ""} ${food.restricciones ?? ""}`);
  return !exclusions.some((exclusion) => exclusion && foodText.includes(exclusion));
}

function foodsForGroup(data, patient, groupId, meal) {
  const rows = tableRows(data, "alimentos")
    .filter(isActive)
    .filter((food) => {
      const foodGroup = groupIdFromValue(food.grupo_alimento || food.categoria);
      return foodGroup === groupId || (foodGroup === "mixto" && groupId !== "grasas");
    })
    .filter((food) => foodAllowedForPatient(food, patient))
    .sort((a, b) => {
      const aMeal = mealMatchesFood(a, meal) ? 0 : 1;
      const bMeal = mealMatchesFood(b, meal) ? 0 : 1;
      if (aMeal !== bMeal) return aMeal - bMeal;
      return (toNumber(a.prioridad) || 99) - (toNumber(b.prioridad) || 99);
    });

  return rows;
}

function templateOptions(data, meal, groupId) {
  const template = tableRows(data, "plantillas_esquema_diario")
    .filter(isActive)
    .find((row) => normalizeText(row.tiempo_comida) === normalizeText(meal)
      && groupIdFromValue(row.grupo_macro) === groupId);
  return template ? splitList(template.opciones) : [];
}

function formatPortionCount(value) {
  return Number(value.toFixed(1)).toLocaleString("es-CO");
}

function formatPortionLabel(value) {
  return `${formatPortionCount(value)} ${Number(value.toFixed(1)) === 1 ? "porcion" : "porciones"}`;
}

function roundGrams(value) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (value < 20) return Math.round(value);
  return Math.round(value / 5) * 5;
}

function buildOptionsText(data, patient, groupId, meal) {
  const foods = foodsForGroup(data, patient, groupId, meal);
  const fromTemplates = templateOptions(data, meal, groupId);
  const fromFoods = foods.map((food) => food.nombre);
  const seen = new Set();
  const merged = [];
  for (const option of [...fromTemplates, ...fromFoods, ...optionsByGroup[groupId]]) {
    const key = normalizeText(option);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push(option);
  }
  return merged.slice(0, 6).join(", ");
}

function buildExampleText(data, patient, group, mealDistribution, portionCount, standard) {
  const foods = foodsForGroup(data, patient, group.id, mealDistribution.meal);
  const selectedFood = foods[0];
  const targetMacro = Math.round(mealDistribution[group.macroKey]);
  const grams = roundGrams((selectedFood?.porcion_estandar_g || standard.grams) * portionCount);
  const measure = selectedFood?.medida_casera || standard.measure;
  const foodName = selectedFood?.nombre || group.label.toLowerCase();
  const source = standard.source ? ` Base: ${standard.source}.` : "";

  return `${formatPortionLabel(portionCount)} = ${grams} g aprox (${measure} por porcion) de ${foodName}. Objetivo: ${targetMacro} g de ${group.macroLabel}.${source}`;
}

export function distributePortions(patient, data = null) {
  const macros = calculateMacros(patient);
  const meals = activeMealTimes(patient);
  const standards = getPortionStandards(data);
  const weights = {
    desayuno: 0.2,
    media_manana: 0.1,
    racion_preentrenamiento: 0.1,
    racion_postentrenamiento: 0.12,
    almuerzo: 0.28,
    media_tarde: 0.1,
    cena: 0.2,
  };
  const totalWeight = meals.reduce((sum, meal) => sum + (weights[meal] ?? 0.1), 0);
  return meals.map((meal) => {
    const share = (weights[meal] ?? 0.1) / totalWeight;
    return {
      meal,
      kcal: Math.round(macros.calories * share),
      protein: Math.round(macros.protein * share),
      carbs: Math.round(macros.carbs * share),
      fat: Math.round(macros.fat * share),
      portions: {
        proteinas: calculatePortionCount(macros.protein * share, standards.proteinas.macroGrams),
        carbohidratos: calculatePortionCount(macros.carbs * share, standards.carbohidratos.macroGrams),
        grasas: calculatePortionCount(macros.fat * share, standards.grasas.macroGrams),
      },
    };
  });
}

export function mealLabel(key) {
  return {
    desayuno: "Desayuno",
    media_manana: "Media manana",
    racion_preentrenamiento: "Racion preentrenamiento",
    racion_postentrenamiento: "Racion postentrenamiento",
    almuerzo: "Almuerzo",
    media_tarde: "Media tarde",
    cena: "Cena",
  }[key] ?? key;
}

export function buildDailyScheme(patient, data = null) {
  const standards = getPortionStandards(data);
  return distributePortions(patient, data).flatMap((item) => macroGroups.map((group) => {
    const portionCount = item.portions[group.id];
    return {
      id: `${item.meal}_${group.rowId}`,
      tiempo: mealLabel(item.meal),
      grupo: group.label,
      opciones: buildOptionsText(data, patient, group.id, item.meal),
      ejemplo: buildExampleText(data, patient, group, item, portionCount, standards[group.id]),
      porciones: portionCount,
    };
  }));
}

const menuTemplates = {
  desayuno: [
    "Avena cocida con banano y huevo",
    "Yogurt griego natural con avena y fruta",
    "Huevos pericos con arepa y aguacate",
  ],
  media_manana: [
    "Yogurt griego con fruta",
    "Banano con frutos secos",
    "Tostadas de arroz con queso bajo en grasa",
  ],
  racion_preentrenamiento: [
    "Banano y agua",
    "Avena ligera con fruta",
    "Tostadas de arroz con mermelada baja en azucar",
  ],
  racion_postentrenamiento: [
    "Yogurt griego con cereal",
    "Batido proteico con fruta",
    "Sanduche de pollo y fruta",
  ],
  almuerzo: [
    "Pollo a la plancha con arroz integral, ensalada y aguacate",
    "Bowl de lentejas con arroz, vegetales y aceite de oliva",
    "Pescado con papa, vegetales y aguacate",
  ],
  media_tarde: [
    "Fruta con yogurt",
    "Arepa pequena con huevo",
    "Avena fria con canela",
  ],
  cena: [
    "Tortilla de huevo con vegetales y aguacate",
    "Pollo guisado con ensalada y arroz",
    "Lentejas con ensalada y huevo",
  ],
};

function cleanSentenceFragment(value) {
  return String(value ?? "").trim().replace(/[.\s]+$/g, "");
}

function menuTemplateText(row) {
  const preparation = cleanSentenceFragment(row.preparacion_sugerida || row.nombre || "Preparacion editable");
  const parts = [
    `${preparation}.`,
    row.alimentos ? `Alimentos: ${cleanSentenceFragment(row.alimentos)}.` : "",
    row.cantidades ? `Cantidades: ${cleanSentenceFragment(row.cantidades)}.` : "",
    row.medida_casera ? `Medidas caseras: ${cleanSentenceFragment(row.medida_casera)}.` : "",
    row.observaciones ? `Observaciones: ${cleanSentenceFragment(row.observaciones)}.` : "",
  ].filter(Boolean);
  return `${parts.join(" ")} Ajustar cantidades segun esquema diario.`;
}

function findMenuTemplate(data, meal, dayIndex) {
  const templates = tableRows(data, "plantillas_menu_7_dias")
    .filter(isActive)
    .filter((row) => normalizeText(row.tiempo_comida) === normalizeText(meal));
  if (!templates.length) return null;
  const exactDay = templates.find((row) => toNumber(row.dia) === dayIndex + 1);
  return exactDay || templates[dayIndex % templates.length];
}

export function generateMenu(patient, data = null) {
  const meals = activeMealTimes(patient);
  return Array.from({ length: 7 }, (_, dayIndex) => ({
    day: dayIndex + 1,
    meals: meals.map((meal) => {
      const editableTemplate = findMenuTemplate(data, meal, dayIndex);
      if (editableTemplate) {
        return {
          meal,
          label: mealLabel(meal),
          text: menuTemplateText(editableTemplate),
        };
      }
      const templates = menuTemplates[meal] ?? ["Preparacion editable"];
      return {
        meal,
        label: mealLabel(meal),
        text: `${templates[dayIndex % templates.length]}. Ajustar cantidades segun esquema diario.`,
      };
    }),
  }));
}

function appliesToPatient(value, patient) {
  const tokens = splitList(value).map(normalizeText).filter(Boolean);
  if (!tokens.length || tokens.includes("todos")) return true;
  const objective = normalizeText(patient?.objetivo_principal);
  const objectiveLabel = normalizeText(objectiveRules[patient?.objetivo_principal]?.label);
  const isAthlete = toNumber(patient?.entrenamientos_semana) > 0 || Boolean(String(patient?.deporte ?? "").trim());
  const patientText = normalizeText([
    patient?.objetivo_secundario,
    patient?.estado_nutricional,
    patient?.condiciones,
    patient?.enfermedades_condiciones,
    patient?.restricciones,
    patient?.intolerancias,
    patient?.alergias,
  ].filter(Boolean).join(" "));

  return tokens.some((token) => {
    if (token === objective || token === objectiveLabel) return true;
    if (token === "deportista" && isAthlete) return true;
    return patientText.includes(token);
  });
}

function sortedRecommendationRows(data, tableName, orderKey = "orden") {
  return tableRows(data, tableName)
    .filter(isActive)
    .sort((a, b) => {
      const orderA = toNumber(a[orderKey] ?? a.prioridad) || 99;
      const orderB = toNumber(b[orderKey] ?? b.prioridad) || 99;
      return orderA - orderB || String(a.nombre ?? "").localeCompare(String(b.nombre ?? ""), "es");
    });
}

function interpolateRecommendationText(text, context) {
  return String(text ?? "").replace(/\{([a-zA-Z0-9_]+)\}/g, (_, key) => context[key] ?? "");
}

function pushRecommendation(recs, id, title, text) {
  const safeText = String(text ?? "").trim();
  if (!safeText) return;
  recs.push({
    id,
    title: title || "Recomendacion",
    text: safeText,
  });
}

function patientContextText(patient) {
  return normalizeText([
    patient?.restricciones,
    patient?.intolerancias,
    patient?.alergias,
    patient?.preferencias_alimentarias,
    patient?.alimentos_no_consume,
    patient?.restricciones_culturales,
    patient?.condiciones,
    patient?.enfermedades_condiciones,
  ].filter(Boolean).join(" "));
}

function rowMatchesPatient(row, patient) {
  const patientText = patientContextText(patient);
  if (!patientText) return false;
  const rowText = normalizeText([
    row.nombre,
    row.categoria,
    row.descripcion,
    row.alimentos_a_evitar,
    row.sustitutos,
    row.alerta,
  ].filter(Boolean).join(" "));
  return patientText
    .split(/\s+/)
    .filter((token) => token.length > 4)
    .some((token) => rowText.includes(token))
    || normalizeText(row.nombre).split(/\s+/).some((token) => token.length > 4 && patientText.includes(token));
}

function formatRestrictionRecommendations(data, patient) {
  const matches = tableRows(data, "restricciones_alimentarias")
    .filter(isActive)
    .filter((row) => rowMatchesPatient(row, patient));
  const reported = [
    patient?.restricciones,
    patient?.intolerancias,
    patient?.alergias,
    patient?.alimentos_no_consume,
  ].filter(Boolean).join("; ");
  const parts = matches.map((row) => {
    const avoid = row.alimentos_a_evitar ? `evitar o revisar ${row.alimentos_a_evitar}` : "";
    const substitutes = row.sustitutos ? `sustituir por ${row.sustitutos}` : "";
    const alert = row.alerta ? row.alerta : "";
    return `${row.nombre}: ${[avoid, substitutes, alert].filter(Boolean).map(cleanSentenceFragment).join(". ")}.`;
  });
  if (reported && !parts.length) {
    parts.push(`Restricciones reportadas: ${reported}. Validar sustituciones, etiquetas y tolerancia antes de entregar.`);
  }
  return parts.join(" ");
}

function formatClinicalConditionRecommendations(data, patient) {
  return tableRows(data, "enfermedades_condiciones")
    .filter(isActive)
    .filter((row) => rowMatchesPatient(row, patient))
    .map((row) => `${row.nombre}: ${cleanSentenceFragment(row.alerta || row.descripcion)}.${row.derivacion ? ` Derivar o coordinar con ${row.derivacion}.` : ""}`)
    .join(" ");
}

function formatFatsAndTechniques(data) {
  const fats = tableRows(data, "grasas_recomendadas")
    .filter(isActive)
    .slice(0, 4)
    .map((row) => `${row.nombre} (${row.porcion || "porcion medida"}): ${cleanSentenceFragment(row.uso || row.descripcion)}`);
  const techniques = tableRows(data, "tecnicas_culinarias")
    .filter(isActive)
    .slice(0, 4)
    .map((row) => `${row.nombre}: ${cleanSentenceFragment(row.recomendacion || row.descripcion)}`);
  return [
    fats.length ? `Grasas recomendadas: ${fats.join("; ")}.` : "",
    techniques.length ? `Tecnicas culinarias: ${techniques.join("; ")}.` : "",
  ].filter(Boolean).join(" ");
}

function fallbackRecommendations(patient, meals, objective) {
  return [
    {
      id: "organizacion",
      title: "Organizacion del plan",
      text: "Este plan de alimentación está organizado para seleccionar por tiempo de comida un alimento fuente de proteína, carbohidratos y grasas saludables. De cada alimento fuente se presentan varias opciones para variar la alimentación cada día.",
    },
    {
      id: "tiempos",
      title: "Tiempos de comida",
      text: `El plan usa ${meals.length} tiempos de comida para apoyar el objetivo de ${objective.toLowerCase()} y adaptarse a horarios de entrenamiento y rutina diaria.`,
    },
    {
      id: "desayuno_raciones",
      title: "Desayuno y raciones",
      text: "El desayuno integra una fuente de proteína, carbohidrato y grasa saludable. Las raciones pre o postentrenamiento se activan cuando hay entrenamiento reportado.",
    },
    {
      id: "hidratacion_dia",
      title: "Hidratacion diaria",
      text: "Distribuir el consumo de agua durante el dia y revisar color de orina como guia practica.",
    },
    {
      id: "hidratacion_entreno",
      title: "Hidratacion en entrenamiento",
      text: toNumber(patient.entrenamientos_semana) > 0
        ? "En sesiones largas, intensas o con calor, planear bebida con electrolitos y carbohidratos segun duracion."
        : "Si se inicia entrenamiento, ajustar hidratacion segun duracion, calor e intensidad.",
    },
    {
      id: "limitar",
      title: "Alimentos a limitar",
      text: patient.objetivo_principal === "perdida_grasa"
        ? "Limitar bebidas azucaradas, fritos frecuentes, postres, embutidos y productos de alta densidad energetica."
        : "Mantener baja frecuencia de ultraprocesados y priorizar alimentos frescos.",
    },
    {
      id: "grasas_tecnicas",
      title: "Grasas y tecnicas culinarias",
      text: "Preferir aceite de oliva, aguacate y frutos secos en porciones medidas. Usar plancha, horno, vapor o guisos bajos en grasa.",
    },
    {
      id: "particulares",
      title: "Necesidades particulares",
      text: `${patient.restricciones || "Sin restricciones reportadas"}. Revisar tolerancias, presupuesto y preferencias antes de entregar.`,
    },
  ];
}

export function generateRecommendations(patient, data = null) {
  const meals = activeMealTimes(patient);
  const objective = objectiveRules[patient.objetivo_principal]?.label ?? "objetivo individual";
  if (!data?.tables) return fallbackRecommendations(patient, meals, objective);

  const recs = [];
  const context = {
    objetivo: objective.toLowerCase(),
    objetivo_principal: patient.objetivo_principal ?? "",
    tiempos_comida: meals.length,
    tiempos_comida_lista: meals.map(mealLabel).join(", "),
    deporte: patient.deporte || "actividad reportada",
    presupuesto: patient.presupuesto || "no especificado",
  };
  const baseRows = sortedRecommendationRows(data, "recomendaciones_base")
    .filter((row) => appliesToPatient(row.aplica_a, patient));
  const intro = baseRows.find((row) => normalizeText(row.categoria).includes("introduccion")) || baseRows[0];

  if (intro) {
    pushRecommendation(recs, intro.id, intro.nombre, interpolateRecommendationText(intro.texto, context));
  } else {
    pushRecommendation(recs, "organizacion", "Organizacion del plan", fallbackRecommendations(patient, meals, objective)[0].text);
  }

  pushRecommendation(
    recs,
    "tiempos",
    "Tiempos de comida",
    `El plan usa ${meals.length} tiempos de comida (${meals.map(mealLabel).join(", ")}) para apoyar el objetivo de ${objective.toLowerCase()} y adaptarse a la rutina reportada.`
  );
  pushRecommendation(
    recs,
    "desayuno_raciones",
    "Desayuno y raciones",
    toNumber(patient.entrenamientos_semana) > 0
      ? "El desayuno integra una fuente de proteína, carbohidrato y grasa saludable. Las raciones pre y postentrenamiento se ajustan al horario, duración e intensidad reportadas."
      : "El desayuno integra una fuente de proteína, carbohidrato y grasa saludable. Si se activan raciones, deben ajustarse a hambre, horarios y objetivo."
  );

  const hydrationRows = sortedRecommendationRows(data, "recomendaciones_hidratacion", "prioridad")
    .filter((row) => appliesToPatient(row.condicion, patient));
  for (const row of hydrationRows) {
    pushRecommendation(recs, row.id, row.nombre, interpolateRecommendationText(row.texto, context));
  }

  const trainingRows = sortedRecommendationRows(data, "recomendaciones_entrenamiento", "prioridad")
    .filter((row) => toNumber(patient.entrenamientos_semana) > 0 && appliesToPatient(row.aplica_a, patient));
  for (const row of trainingRows) {
    const moment = row.momento ? `${row.momento}: ` : "";
    pushRecommendation(recs, row.id, row.nombre, `${moment}${interpolateRecommendationText(row.texto, context)}`);
  }

  const qualityRows = baseRows.filter((row) => row !== intro);
  const qualityText = qualityRows.map((row) => interpolateRecommendationText(row.texto, context)).join(" ");
  const restrictionText = formatRestrictionRecommendations(data, patient);
  pushRecommendation(
    recs,
    "limitar",
    "Alimentos a limitar y sustituciones",
    [qualityText, restrictionText].filter(Boolean).join(" ")
      || "Mantener baja frecuencia de ultraprocesados y priorizar alimentos frescos, porciones medidas y sustituciones acordes con tolerancias."
  );

  pushRecommendation(
    recs,
    "grasas_tecnicas",
    "Grasas y tecnicas culinarias",
    formatFatsAndTechniques(data) || fallbackRecommendations(patient, meals, objective).find((item) => item.id === "grasas_tecnicas").text
  );

  const clinicalText = formatClinicalConditionRecommendations(data, patient);
  pushRecommendation(
    recs,
    "particulares",
    "Necesidades particulares",
    [
      clinicalText,
      patient.observaciones_profesionales ? `Observaciones profesionales: ${patient.observaciones_profesionales}.` : "",
      `Presupuesto reportado: ${patient.presupuesto || "no especificado"}. Revisar tolerancias, preferencias y seguridad antes de entregar.`,
    ].filter(Boolean).join(" ")
  );

  return recs;
}

export const shoppingCategories = [
  "Proteínas",
  "Carbohidratos",
  "Frutas y verduras",
  "Grasas saludables",
  "Lácteos",
  "Suplementos/productos",
  "Otros",
];

const shoppingCatalog = [
  { category: "Proteínas", name: "Huevos", note: "Base para desayunos, cenas o raciones.", pattern: /\bhuevo(s)?\b/i },
  { category: "Proteínas", name: "Pollo", note: "Proteína principal para almuerzos, cenas o bowls.", pattern: /\bpollo\b/i },
  { category: "Proteínas", name: "Pescado", note: "Alternativa de proteína para comidas principales.", pattern: /\bpescado\b/i },
  { category: "Proteínas", name: "Lentejas", note: "Proteína vegetal y fuente de fibra.", pattern: /\blenteja(s)?\b/i },
  { category: "Carbohidratos", name: "Avena", note: "Para desayunos, preparaciones frías o raciones.", pattern: /\bavena\b/i },
  { category: "Carbohidratos", name: "Arroz integral", note: "Base de comidas principales.", pattern: /\barroz( integral)?\b/i },
  { category: "Carbohidratos", name: "Papa", note: "Tubérculo para comidas principales.", pattern: /\bpapa(s)?\b/i },
  { category: "Carbohidratos", name: "Arepa", note: "Opción práctica de desayuno o merienda.", pattern: /\barepa(s)?\b/i },
  { category: "Carbohidratos", name: "Tostadas de arroz", note: "Opción ligera para raciones o refrigerios.", pattern: /\btostada(s)? de arroz\b/i },
  { category: "Carbohidratos", name: "Cereal", note: "Para ración postentrenamiento según tolerancia.", pattern: /\bcereal\b/i },
  { category: "Frutas y verduras", name: "Banano", note: "Fruta práctica para desayuno o entrenamiento.", pattern: /\bbanano(s)?\b/i },
  { category: "Frutas y verduras", name: "Fruta variada", note: "Elegir opciones de temporada y preferencia.", pattern: /\bfruta(s)?\b/i },
  { category: "Frutas y verduras", name: "Vegetales", note: "Para ensaladas, bowls y acompañamientos.", pattern: /\bvegetal(es)?\b/i },
  { category: "Frutas y verduras", name: "Ensalada", note: "Comprar verduras frescas para ensaladas.", pattern: /\bensalada(s)?\b/i },
  { category: "Grasas saludables", name: "Aguacate", note: "Grasa saludable para acompañar comidas.", pattern: /\baguacate(s)?\b/i },
  { category: "Grasas saludables", name: "Aceite de oliva", note: "Usar medido para cocción o aderezos.", pattern: /\baceite de oliva\b/i },
  { category: "Grasas saludables", name: "Frutos secos", note: "Porcionar para refrigerios.", pattern: /\bfrutos secos\b/i },
  { category: "Lácteos", name: "Yogurt griego", note: "Lácteo alto en proteína para refrigerios.", pattern: /\byogurt griego\b|\byogurt\b/i },
  { category: "Lácteos", name: "Queso bajo en grasa", note: "Opción láctea para meriendas.", pattern: /\bqueso bajo en grasa\b|\bqueso\b/i },
  { category: "Otros", name: "Mermelada baja en azúcar", note: "Usar en porción pequeña si está incluida.", pattern: /\bmermelada baja en azucar\b|\bmermelada baja en azúcar\b/i },
  { category: "Otros", name: "Canela", note: "Condimento opcional para avena o yogurt.", pattern: /\bcanela\b/i },
];
function normalizeShoppingName(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function emptyShoppingGroups() {
  return shoppingCategories.map((category) => ({ category, items: [] }));
}

function addShoppingItem(groups, index, category, name, note, source) {
  const safeName = String(name ?? "").trim();
  if (!safeName) return;
  const safeCategory = shoppingCategories.includes(category) ? category : "Otros";
  const key = `${safeCategory}|${normalizeShoppingName(safeName)}`;
  const existing = index.get(key);
  if (existing) {
    if (source && !existing.source.split("; ").includes(source)) {
      existing.source = `${existing.source}; ${source}`;
    }
    if (note && !existing.note) existing.note = note;
    return;
  }
  const group = groups.find((item) => item.category === safeCategory);
  const item = {
    name: safeName,
    note: String(note ?? "").trim(),
    source: String(source ?? "Plan").trim(),
  };
  group.items.push(item);
  index.set(key, item);
}

function addCatalogMatches(groups, index, text, source) {
  for (const entry of shoppingCatalog) {
    if (entry.pattern.test(text)) {
      addShoppingItem(groups, index, entry.category, entry.name, entry.note, source);
    }
  }
}

export function generateShoppingList(menu, selectedProductsOrOptions = [], maybeOptions = {}) {
  const options = Array.isArray(selectedProductsOrOptions)
    ? { products: selectedProductsOrOptions, ...maybeOptions }
    : selectedProductsOrOptions ?? {};
  const groups = emptyShoppingGroups();
  const index = new Map();

  for (const day of menu) {
    for (const meal of day.meals) {
      const source = `Día ${day.day} - ${meal.label}`;
      addCatalogMatches(groups, index, String(meal.text ?? ""), source);
    }
  }

  for (const recipe of options.recipes ?? []) {
    const source = `Receta - ${recipe.nombre}`;
    addCatalogMatches(groups, index, String(recipe.ingredientes ?? ""), source);
  }

  for (const product of options.products ?? []) {
    const note = product.motivo_recomendacion || product.descripcion || product.donde_se_consigue || "Producto recomendado.";
    const source = product.donde_se_consigue ? `Producto recomendado - ${product.donde_se_consigue}` : "Producto recomendado";
    addShoppingItem(groups, index, "Suplementos/productos", product.nombre, note, source);
  }

  for (const supplement of options.supplements ?? []) {
    const note = supplement.indicacion_consumo || supplement.observaciones || supplement.descripcion || "Suplemento opcional.";
    addShoppingItem(groups, index, "Suplementos/productos", supplement.nombre, note, "Suplemento seleccionado");
  }

  for (const group of groups) {
    group.items.sort((a, b) => a.name.localeCompare(b.name, "es"));
  }

  return groups.filter((group) => group.items.length > 0);
}
